import type { CSSProperties, ReactNode } from "react";
import { MailShellHeader } from "./MailShellHeader";
import { MailSplitResizer } from "./MailSplitResizer";
import { useMailSplitLayout } from "@/hooks/useMailSplitLayout";
import { MAIL_SPLIT_PREVIEW_LIST } from "@/lib/mail/mailSplitLayoutPrefs";
import { cn } from "@/lib/cn";

type Props = {
  sidebar: ReactNode;
  list: ReactNode;
  reading: ReactNode;
  headerActions?: ReactNode;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  searchDisabled?: boolean;
  searchBusy?: boolean;
  detailOpen?: boolean;
  readingToolbar?: ReactNode;
  bootstrapBar?: ReactNode;
  /** Verrouille le redimensionnement automatique des panneaux (modale compose). */
  freezeSplitLayout?: boolean;
  /** Liste compacte, lecteur élargi (desktop, non persisté, déclenché à la main). */
  previewExpandReading?: boolean;
  /** Chevron sur le séparateur liste / lecture. */
  onToggleReadingExpand?: () => void;
  showReadingExpandToggle?: boolean;
};

export function MailShell({
  sidebar,
  list,
  reading,
  headerActions,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  searchDisabled,
  searchBusy,
  detailOpen = false,
  readingToolbar,
  bootstrapBar,
  freezeSplitLayout = false,
  previewExpandReading = false,
  onToggleReadingExpand,
  showReadingExpandToggle = false,
}: Props) {
  const { rootRef, layout, startNavResize, startListResize } = useMailSplitLayout({
    // Largeur réglée jamais écrasée pendant l’élargissement temporaire.
    freezeLayout: freezeSplitLayout || previewExpandReading,
  });
  const listWidth = previewExpandReading
    ? Math.min(layout.listWidth, MAIL_SPLIT_PREVIEW_LIST)
    : layout.listWidth;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <MailShellHeader
        actions={headerActions}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        searchDisabled={searchDisabled}
        searchBusy={searchBusy}
      />
      {bootstrapBar}

      <div
        ref={rootRef}
        className={cn(
          "mail-split-root min-h-0 min-w-0 flex-1 overflow-hidden p-2 md:p-3",
          previewExpandReading && "mail-split-root--preview-expand"
        )}
        style={
          {
            "--mail-nav-w": `${layout.navWidth}px`,
            "--mail-list-w": `${listWidth}px`,
          } as CSSProperties
        }
      >
        <aside
          className={cn(
            "mail-panel mail-split-nav min-h-0 overflow-hidden max-md:hidden md:flex md:flex-col"
          )}
        >
          {sidebar}
        </aside>

        <MailSplitResizer
          label="Redimensionner la barre latérale"
          onPointerDown={startNavResize}
        />

        <section
          className={cn(
            "mail-panel mail-split-list min-h-0 min-w-0 flex-1 overflow-hidden md:flex-none",
            detailOpen && "max-md:hidden"
          )}
        >
          {list}
        </section>

        <MailSplitResizer
          label="Redimensionner la liste des messages"
          onPointerDown={startListResize}
          disabled={previewExpandReading}
          expandToggle={
            showReadingExpandToggle && onToggleReadingExpand
              ? { expanded: previewExpandReading, onToggle: onToggleReadingExpand }
              : undefined
          }
        />

        <section
          className={cn(
            "mail-panel mail-split-reading flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden",
            detailOpen
              ? "max-md:absolute max-md:inset-2 max-md:z-20 max-md:flex"
              : "max-md:hidden",
            "md:flex"
          )}
        >
          {detailOpen ? readingToolbar : null}
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{reading}</div>
        </section>
      </div>
    </div>
  );
}
